import {
  GridColDef,
  GridLocaleText,
  GridRenderCellParams,
} from "@mui/x-data-grid"
import clsx from "clsx"
import { IntlShape } from "react-intl"
import { Field } from "../Enum"
import { Buyer, RecordType } from "../Type"
import { getRecordType } from "../Utils"
import { formatCurrencyAmount } from "./CurrencyUtils"
import { formatDate } from "./DateUtils"

/**
 * Translate DataGrid default header items (Filter, Density etc.) into Bengali or English.
 * Full translation keys: https://github.com/mui-org/material-ui-x/blob/HEAD/packages/grid/_modules_/grid/constants/localeTextConstants.ts
 */
export const getTranslatedDataGridToolbar = (
  intl: IntlShape
): Partial<GridLocaleText> => {
  return {
    toolbarColumns: intl.formatMessage({
      id: "table.toolbar.column",
    }),
    toolbarFilters: intl.formatMessage({
      id: "table.toolbar.filter",
    }),
    toolbarDensity: intl.formatMessage({
      id: "table.toolbar.density",
    }),
    toolbarDensityCompact: intl.formatMessage({
      id: "table.toolbar.density.option.compact",
    }),
    toolbarDensityStandard: intl.formatMessage({
      id: "table.toolbar.density.option.standard",
    }),
    toolbarDensityComfortable: intl.formatMessage({
      id: "table.toolbar.density.option.comfortable",
    }),
    toolbarExport: intl.formatMessage({
      id: "table.toolbar.export",
    }),
    toolbarExportCSV: intl.formatMessage({
      id: "table.toolbar.export.option.csv",
    }),
    toolbarExportPrint: intl.formatMessage({
      id: "table.toolbar.export.option.print",
    }),
  }
}

const renderDateCell = (params: any) => {
  return formatDate(params.value)
}

const renderBuyerNameCell = (params: any, buyers: Buyer[]) => {
  return buyers.filter((buyer) => buyer.id === params.value)[0]
    ? buyers.filter((buyer) => buyer.id === params.value)[0].name
    : "Unknown"
}

const renderTotalPriceCell = (params: any) => {
  return formatCurrencyAmount(params.value)
}

/**
 * Invoice list column headers and cell value setup
 */

export const getInvoiceListColumns = (
  intl: IntlShape,
  buyers: Buyer[]
): GridColDef[] => {
  return [
    {
      field: Field.InsertDate,
      headerName: intl.formatMessage({
        id: "table.headers.date",
      }),
      align: "center",
      headerAlign: "center",
      renderCell: renderDateCell,
    },
    {
      field: Field.BuyerID,
      headerName: intl.formatMessage({
        id: "table.headers.customer",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return renderBuyerNameCell(params, buyers)
      },
    },
    {
      field: Field.TotalItems,
      headerName: intl.formatMessage({
        id: "table.headers.items",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: Field.TotalPrice,
      headerName: intl.formatMessage({
        id: "table.headers.price",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: renderTotalPriceCell,
    },
  ]
}

const renderAmountCell = (params: any) => {
  return formatCurrencyAmount(params.row.amount)
}

const renderRecTypeCell = (params: any, recordTypes: RecordType[]) => {
  return getRecordType(params.row, recordTypes).name
}

const renderNoteCell = (params: any) => {
  return params.row.note
}

export const getRecordListColumns = (
  intl: IntlShape,
  recTypes: RecordType[]
): GridColDef[] => {
  return [
    {
      field: Field.Date,
      headerName: intl.formatMessage({
        id: "table.headers.date",
      }),
      renderCell: renderDateCell,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: Field.Amount,
      headerName: intl.formatMessage({
        id: "table.headers.amount",
      }),
      type: "number",
      renderCell: renderAmountCell,
      align: "center",
      headerAlign: "center",
      flex: 1,
      cellClassName: (params: any) =>
        // bookkeeper class is defined inside the <Box> element sx
        clsx("bookkeeper", {
          negative: params.row.credit ? false : true,
          positive: params.row.credit ? true : false,
        }),
    },
    {
      field: "type",
      headerName: intl.formatMessage({
        id: "table.headers.type",
      }),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return renderRecTypeCell(params, recTypes)
      },
      align: "center",
      headerAlign: "center",
    },
    {
      field: Field.Note,
      headerName: intl.formatMessage({
        id: "table.headers.note",
      }),
      flex: 1,
      renderCell: renderNoteCell,
      align: "center",
      headerAlign: "center",
    },
  ]
}

const renderUnitPriceCell = (params: any) => {
  return formatCurrencyAmount(params.value)
}

export const getInventoryListColumns = (intl: IntlShape): GridColDef[] => {
  return [
    {
      field: Field.Name,
      headerName: intl.formatMessage({
        id: "table.headers.name",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: Field.UnitPrice,
      headerName: intl.formatMessage({
        id: "table.headers.unit.price",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: renderUnitPriceCell,
    },
    {
      field: Field.Quantity,
      headerName: intl.formatMessage({
        id: "table.headers.quantity",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
      cellClassName: (params: any) =>
        // bookkeeper class is defined inside the <Box> element sx
        clsx("bookkeeper", {
          negative: params.row.quantity < 10 ? true : false,
          positive: params.row.quantity >= 10 ? true : false,
        }),
    },
  ]
}

export const getBuyerListColumns = (intl: IntlShape): GridColDef[] => {
  return [
    {
      field: Field.Name,
      headerName: intl.formatMessage({
        id: "table.headers.name",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: Field.Address,
      headerName: intl.formatMessage({
        id: "table.headers.address",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: Field.Phone,
      headerName: intl.formatMessage({
        id: "table.headers.phone",
      }),
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
  ]
}
